// pageCache — stale-while-revalidate data cache for the Wing dashboard tabs.
//
// Why this exists:
//   The bottom-nav routes (Dashboard, Nutrition, Coach, Social, Settings)
//   each refetch their data on every mount, so tab switches felt sluggish.
//   This module:
//     1. Caches the last-fetched payload per `key` in memory + localStorage.
//     2. Serves the cached value INSTANTLY on the next subscribe (so the page
//        renders without a blank/skeleton flash) — even after an app cold-start.
//     3. Triggers a background refetch unless `staleAfterMs` hasn't elapsed.
//     4. Notifies all subscribers when fresh data lands.
//
// API:
//   import { getCached, setCache, prefetch, usePageData, invalidate } from "@/lib/pageCache";
//   usePageData("dashboard", () => api.get("/health/day").then(r => r.data));
//
// Why not React Query?
//   We want a 60-line drop-in instead of a multi-file refactor, and we already
//   have ~10 fetch sites to migrate.

import { useEffect, useRef, useState } from "react";

const STORAGE_PREFIX = "wing.cache.v1.";
const DEFAULT_STALE_MS = 30_000;       // refetch in background after 30 s
const DEFAULT_GC_MS = 24 * 60 * 60_000; // drop entries older than 24 h
// Persist to localStorage so cold-start renders are also instant. We still
// cap reads via DEFAULT_GC_MS so a stale cache doesn't haunt the user days
// later — fresh data always overwrites within `staleAfterMs`.
const storage = (typeof window !== "undefined" && window.localStorage) || null;

// In-memory mirror so we don't pay sessionStorage parse cost every read.
const mem = new Map();
// Subscribers per key, so all mounted views update when data changes.
const subs = new Map();
// In-flight promises (dedup concurrent fetches).
const flights = new Map();

function readStorage(key) {
    if (!storage) return null;
    try {
        const raw = storage.getItem(STORAGE_PREFIX + key);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object") return null;
        if (Date.now() - parsed.ts > DEFAULT_GC_MS) return null;
        return parsed;
    } catch { return null; }
}

function writeStorage(key, entry) {
    if (!storage) return;
    try {
        storage.setItem(STORAGE_PREFIX + key, JSON.stringify(entry));
    } catch { /* quota / private mode — ignore */ }
}

function notify(key, entry) {
    const set = subs.get(key);
    if (!set) return;
    set.forEach((fn) => fn(entry));
}

export function getCached(key) {
    if (mem.has(key)) return mem.get(key);
    const stored = readStorage(key);
    if (stored) mem.set(key, stored);
    return stored;
}

export function setCache(key, data) {
    const entry = { data, ts: Date.now() };
    mem.set(key, entry);
    writeStorage(key, entry);
    notify(key, entry);
}

/** Force-evict a key (useful after a mutation). */
export function invalidate(key) {
    mem.delete(key);
    try { storage && storage.removeItem(STORAGE_PREFIX + key); } catch { /* noop */ }
    notify(key, null);
}

/** Force-evict all keys whose name starts with `prefix`. Useful after a
 *  mutation that affects multiple cached views (e.g. logging a meal
 *  invalidates `dashboard:*` AND `nutrition:*`). */
export function invalidatePrefix(prefix) {
    [...mem.keys()].forEach((k) => {
        if (k.startsWith(prefix)) invalidate(k);
    });
    if (!storage) return;
    try {
        for (let i = storage.length - 1; i >= 0; i--) {
            const k = storage.key(i);
            if (k && k.startsWith(STORAGE_PREFIX + prefix)) {
                storage.removeItem(k);
            }
        }
    } catch { /* noop */ }
}

/** Wipe everything — call on logout. */
export function clearAll() {
    mem.clear();
    if (!storage) return;
    try {
        for (let i = storage.length - 1; i >= 0; i--) {
            const k = storage.key(i);
            if (k && k.startsWith(STORAGE_PREFIX)) storage.removeItem(k);
        }
    } catch { /* noop */ }
}

/** Fire a fetch in the background; resolves to the data. Dedups concurrent
 *  callers by `key`. */
export async function prefetch(key, fetcher) {
    if (flights.has(key)) return flights.get(key);
    const p = (async () => {
        try {
            const data = await fetcher();
            setCache(key, data);
            return data;
        } finally {
            flights.delete(key);
        }
    })();
    flights.set(key, p);
    return p;
}

/**
 * usePageData(key, fetcher, opts?)
 *   - Returns { data, loading, error, refresh }.
 *   - First render: data = cached (instant) if present, else null + loading.
 *   - In background: refetches if cache is stale (`staleAfterMs`).
 *   - `enabled = false` skips fetching (e.g. waiting for a dependency).
 */
export function usePageData(key, fetcher, opts = {}) {
    const { staleAfterMs = DEFAULT_STALE_MS, enabled = true } = opts;
    const cached = getCached(key);
    const [entry, setEntry] = useState(cached);
    const [loading, setLoading] = useState(!cached && enabled);
    const [error, setError] = useState(null);
    const fetcherRef = useRef(fetcher);
    fetcherRef.current = fetcher;

    useEffect(() => {
        if (!enabled) return undefined;
        let cancelled = false;
        const set = subs.get(key) || new Set();
        const handler = (e) => { if (!cancelled) setEntry(e); };
        set.add(handler);
        subs.set(key, set);

        const run = async (force = false) => {
            const current = getCached(key);
            const fresh = current && Date.now() - current.ts < staleAfterMs;
            if (fresh && !force) return;
            if (!current) setLoading(true);
            try {
                const data = await prefetch(key, () => fetcherRef.current());
                if (!cancelled) {
                    setError(null);
                    // entry is already updated through notify -> handler
                }
                return data;
            } catch (e) {
                if (!cancelled) setError(e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        run();

        return () => {
            cancelled = true;
            set.delete(handler);
            if (set.size === 0) subs.delete(key);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key, enabled, staleAfterMs]);

    const refresh = () => prefetch(key, () => fetcherRef.current());

    return { data: entry?.data ?? null, loading, error, refresh };
}
